import { useMemo } from 'react';
import { useDebounce } from 'use-debounce';

import { buildClientImgixUrl, isImgixUrl, needsImgixServerSign, resolveByosImgixSrc } from '@air/component-imgix-image';

const DEBOUNCE_DELAY = 150;

export type UseOptimizedImgixSrcParams = {
  src?: string;
  containerWidth: number;
};

export const useOptimizedImgixSrc = ({ src, containerWidth }: UseOptimizedImgixSrcParams) => {
  const [debouncedContainerWidth] = useDebounce(Math.round(containerWidth), DEBOUNCE_DELAY, { trailing: true });

  const optimizedImgixSrc = useMemo(() => {
    if (!src) {
      return undefined;
    }

    const resolvedSrc = resolveByosImgixSrc(src);

    if (isImgixUrl(resolvedSrc)) {
      // Attempt to specify an image suited to the current dimensions of the container
      // If the image already has width/height parameters, use the original image
      try {
        const url = new URL(resolvedSrc);
        const width = url.searchParams.get('w');
        const height = url.searchParams.get('h');

        if (width || height) {
          return needsImgixServerSign(resolvedSrc) ? buildClientImgixUrl(resolvedSrc) : resolvedSrc;
        }

        return buildClientImgixUrl(resolvedSrc, {
          w: debouncedContainerWidth,
          auto: 'format',
          fit: 'max',
        });
      } catch {
        return resolvedSrc;
      }
    }

    return resolvedSrc;
  }, [src, debouncedContainerWidth]);

  return { optimizedImgixSrc };
};
